import React, { useEffect, useState } from "react";
import { Button, Badge, Modal, Upload, message, Spin } from "antd";
import moment from "moment";
import axios from "axios";
import { useTrainingDetail } from "PlatForms/OrgLMS/Hooks/LearnerHooks/useTrainingDetail";
import api from "Common/Client/Interceptor";

const helper = (trainingId, fetchPendingReports) => {
  const [submitFileList, setSubmitFileList] = useState([]);
  const [resubmitFileList, setResubmitFileList] = useState([]);
  const [submitModalVisible, setSubmitModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [loader, setLoader] = useState(null);
  const [currentTask, setCurrentTask] = useState(null);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const token = localStorage.getItem("token");
  const { projectData, fetchProjects } = useTrainingDetail(trainingId);
  const [selectedDownloadingId, setSelectedDownloadingId] = useState(null);

  // Get tasks from projectData
  const startedTasks = projectData?.tasks || [];

  useEffect(() => {
    fetchProjects();
  }, [trainingId]);

  const pendingTasks = projectData?.tasks?.filter((task) => task.status === 1) || [];
  const inProgressTasks =
    projectData?.tasks?.filter(
      (task) => task.status === 2 || task.status === 3 || task.status === 4
    ) || [];
  // Start Task Handler
  const handleStartTask = async (task) => {
    try {
      setLoader(task.id);
      await axios.post(
        `${process.env.REACT_APP_API_URL}/Training/Mark-As-Started`,
        {
          memberId: task.groupMemberId,
          taskId: task.id,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Immediately refetch the updated data
      message.success("Task started successfully!");
      fetchProjects();
      fetchPendingReports();
    } catch (error) {
      message.error("Error starting task");
      console.error(error);
    } finally {
      setLoader(null);
    }
  };

  // Prepare Submit Task Modal
  const handleSubmitTask = (task) => {
    setCurrentTask(task);
    setSubmitModalVisible(true);
    setSubmitFileList([]);
  };

  // View Submission Details
  const handleViewSubmission = (task) => {
    setSelectedSubmission(task);
    setViewModalVisible(true);
    setResubmitFileList([]);
  };

  // Common file upload handler
  const handleFileUpload = async (fileList, isResubmitting = false) => {
    if (!fileList || fileList.length === 0) {
      message.error("Please select a file to upload");
      return false;
    }

    const taskToUse = isResubmitting ? selectedSubmission : currentTask;
    if (!taskToUse) {
      message.error("No task selected");
      return false;
    }

    setSubmitting(true);

    try {
      const formData = new FormData();
      formData.append("MemberId", taskToUse.groupMemberId.toString());
      formData.append("TaskId", taskToUse.id.toString());
      formData.append("IsResubmitting", isResubmitting.toString());

      const file = fileList[0].originFileObj || fileList[0];
      if (!(file instanceof Blob)) {
        throw new Error("Selected file is not valid");
      }
      formData.append("File", file, file.name);

      await api.post("/Training/Submit-Task", formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      message.success(`Task ${isResubmitting ? "resubmitted" : "submitted"} successfully!`);
      setSubmitModalVisible(false);
      setViewModalVisible(false);
      setSubmitFileList([]);
      setResubmitFileList([]);
      fetchProjects();
      return true;
    } catch (error) {
      console.error("Error", error);
    } finally {
      setSubmitting(false);
    }
  };

  // Table Columns Configuration
  const taskColumns = [
    { title: "Task", dataIndex: "title", key: "title" },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const statusMap = {
          1: { badge: "error", text: "Pending" },
          2: { badge: "processing", text: "In Progress" },
          3: { badge: "success", text: "Submitted" },
        };
        const { badge, text } = statusMap[status] || { badge: "default", text: "Unknown" };
        return <Badge status={badge} text={text} />;
      },
    },
    {
      title: "Deadline",
      dataIndex: "deadline",
      key: "deadline",
      render: (text) => (text ? moment(text).format("DD-MM-YYYY") : "N/A"),
    },
     {
      title: "Task File",
      dataIndex: "taskAssignFileName",
      key: "taskAssignFileName",
      render: (text, record) => {
        const fileAvailable = !!text;
        return fileAvailable ? (
          <Button
            type="link"
            style={{ padding: 0 }}
            onClick={() => handleDownloadFile(record.id, 1, record.taskAssignFileName)}
          >
            {selectedDownloadingId === record.id ? <Spin size="small" /> : text}
          </Button>
        ) : (
          "N/A"
        );
      },
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) =>
        record.status === 1 && (
          <Button
            type="primary"
            onClick={() => handleStartTask(record)}
            loading={loader === record.id}
            disabled={loader}
          >
            {loader === record.id ? "Starting..." : "Start Task"}
          </Button>
        ),
    },
  ];

  const inProgressColumns = [
    { title: "Task", dataIndex: "title", key: "title" },
    {
      title: "Status",
      key: "status",
      render: (_, record) => {
        let badgeStatus;
        let text;

        switch (record.status) {
          case 3:
            badgeStatus = "success";
            text = "Submitted";
            break;
          case 4:
            badgeStatus = "success";
            text = "Reviewed & Scored"; // Or any custom text you want
            break;
          default:
            badgeStatus = "processing";
            text = "In Progress";
        }

        return <Badge status={badgeStatus} text={text} />;
      },
    },
    {
      title: "Task File",
      dataIndex: "taskAssignFileName",
      key: "taskAssignFileName",
      render: (text, record) => {
        const fileAvailable = !!text;
        return fileAvailable ? (
          <Button
            type="link"
            style={{ padding: 0 }}
            onClick={() => handleDownloadFile(record.id, 1, record.taskAssignFileName)}
          >
            {selectedDownloadingId === record.id ? <Spin size="small" /> : text}
          </Button>
        ) : (
          "N/A"
        );
      },
    },
    {
      title: "Task Score",
      dataIndex: "mentorScore",
      key: "mentorScore",
      render: (text, record) => {
        if (record.status === 1 || record.status === 2) {
          // If status is In Progress or any other status that shouldn't show score
          return <span>---</span>;
        }

        // If status is 3 or 4 (Submitted or Reviewed & Scored)
        return (
          <span style={{ color: text ? "inherit" : "gray" }}>
            {text || "Score not yet provided by Mentor"}
          </span>
        );
      },
    },

    {
      title: "Action",
      key: "action",
      render: (_, record) =>
        record.status === 3 || record.status === 4 ? (
          <Button type="link" onClick={() => handleViewSubmission(record)}>
            View Submission
          </Button>
        ) : (
          <Button type="primary" onClick={() => handleSubmitTask(record)}>
            Submit Task
          </Button>
        ),
    },
  ];

  // Upload Configuration
  const getUploadProps = (fileList, setFileList) => ({
    beforeUpload: (file) => {
      const validTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];
      const isLt40M = file.size / 1024 / 1024 < 10;

      if (!validTypes.includes(file.type)) {
        message.error("You can only upload PDF or Word files!");
        return false;
      }
      if (!isLt40M) {
        message.error("File must be smaller than 40MB!");
        return false;
      }

      setFileList([file]);
      return false;
    },
    onRemove: () => setFileList([]),
    fileList,
    accept: ".pdf,.doc,.docx",
    maxCount: 1,
  });

  // Modal Components
  const SubmitModal = (
    <Modal
      title="Submit Task"
      open={submitModalVisible}
      onOk={() => handleFileUpload(submitFileList, false)}
      onCancel={() => {
        setSubmitModalVisible(false);
        setSubmitFileList([]);
      }}
      confirmLoading={submitting}
      centered
      okText="Submit"
    >
      <Upload {...getUploadProps(submitFileList, setSubmitFileList)}>
        <Button>Select File</Button>
      </Upload>
    </Modal>
  );

  const handleDownloadFile = async (taskId, fileType, fileName) => {
    try {
      setSelectedDownloadingId(taskId);
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/Training/Download-Task`,
        {},
        {
          params: {
            taskId,
            taskFileType: fileType,
          },
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "*/*",
          },
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName || "file");
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Download failed:", error.response || error.message);
    } finally {
      setSelectedDownloadingId(null);
    }
  };

  const ViewModal = (
    <Modal
      title="Submitted File Details"
      open={viewModalVisible}
      onCancel={() => setViewModalVisible(false)}
      footer={null}
      centered
    >
      {selectedSubmission && (
        <div>
          <p>
            <strong>Submitted File:</strong> {selectedSubmission.submittedFileName}
          </p>
          <Button
            type="link"
            onClick={() =>
              handleDownloadFile(selectedSubmission.id, 2, selectedSubmission.submittedFileName)
            }
            style={{ padding: 0 }}
          >
            {selectedDownloadingId === selectedSubmission.id ? (
              <Spin size="small" />
            ) : (
              "Click to Preview Submitted File"
            )}
          </Button>

          {selectedSubmission.enableSubmission ? (
            <>
              <div style={{ marginTop: 16 }}>
                <Upload {...getUploadProps(resubmitFileList, setResubmitFileList)}>
                  <Button>Select New File</Button>
                </Upload>
              </div>
              <div style={{ marginTop: 16, textAlign: "right" }}>
                <Button
                  type="primary"
                  onClick={() => handleFileUpload(resubmitFileList, true)}
                  disabled={resubmitFileList.length === 0}
                  loading={submitting}
                >
                  Resubmit
                </Button>
              </div>
            </>
          ) : (
            <p style={{ marginTop: 16, fontStyle: "italic" }}>
              You can resubmit when your mentor has enabled it.
            </p>
          )}
        </div>
      )}
    </Modal>
  );

  return {
    taskColumns,
    inProgressColumns,
    startedTasks,
    SubmitModal,
    ViewModal,
    handleSubmitTask,
    inProgressTasks,
    pendingTasks,
  };
};

export default helper;
